import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { paymentService } from '../../services/paymentService';

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [error, setError] = useState(null);
  
  const { bookingData, flightData, passengers, totalAmount } = location.state || {};

  // Clear all saved form data on successful booking
  useEffect(() => {
    // Clear passenger form data from both sessionStorage and sessionStorage
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userId = user.id;
    
    // Clear from sessionStorage
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('passenger_form_data_') || key.startsWith('payment_address_data_') || key === 'currentBookingStep')) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Clear from sessionStorage (user-specific passenger data)
    const localKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('passenger_form_data_')) {
        // Only clear current user's data
        if (userId && key.includes(`passenger_form_data_${userId}_`)) {
          localKeysToRemove.push(key);
        }
      }
    }
    localKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    console.log('🧹 Cleared all saved booking form data after successful booking');
  }, []);

  useEffect(() => {
    // Check if we're coming from a payment gateway callback
    const sessionId = searchParams.get('session_id');
    const paymentId = searchParams.get('payment_id');
    
    if (sessionId) {
      // Stripe payment callback
      verifyStripePayment(sessionId);
    } else if (paymentId) {
      // PayPal payment callback
      setLoading(false);
      // Payment already processed by backend, just show success
    } else if (bookingData) {
      // Direct navigation with booking data
      setBookingInfo(bookingData);
      setLoading(false);
    } else {
      // No valid data, redirect to home
      navigate('/search-flight');
    }
  }, [searchParams, bookingData]);

  const verifyStripePayment = async (sessionId) => {
    try {
      const response = await paymentService.verifyStripePayment(sessionId);
      
      if (response.success) {
        setBookingInfo(response.data);
      } else {
        setError('Payment verification failed');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError('Failed to verify payment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 text-center">
          <img 
            className="w-24 h-24 mx-auto mb-4" 
            src="/assets/images/flight_logo.gif" 
            alt="Loading" 
          />
          <p className="text-jet-text">Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-32 h-32 mx-auto mb-6 flex items-center justify-center">
            <svg className="w-32 h-32 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h4 className="text-2xl font-bold text-red-600 mb-3">Payment Verification Failed</h4>
          <p className="text-jet-text mb-6">{error}</p>
          <button
            onClick={() => navigate('/search-flight')}
            className="w-full py-3 bg-jet-primary text-white rounded-lg font-bold hover:bg-jet-primary-dark transition-colors shadow-md"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 text-center">
        <img 
          className="w-32 h-32 mx-auto mb-6" 
          src="/assets/images/payment.svg" 
          alt="Success" 
        />
        <h4 className="text-2xl font-bold text-jet-primary mb-3">Payment Successful</h4>
        <p className="text-jet-text mb-4">
          Your Flight Booking Status: <span className="text-green-600 font-semibold">Confirmed</span>
        </p>
        {bookingInfo && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-jet-text mb-2">
              <span className="font-semibold">Booking Reference:</span> {bookingInfo.reference_number}
            </p>
            <p className="text-sm text-jet-text">
              <span className="font-semibold">E-Ticket:</span> {bookingInfo.e_ticket_number}
            </p>
          </div>
        )}
        <p className="text-jet-text-light mb-3">
          You can also access this status from<br />booking history.
        </p>
        <p className="text-jet-text-light mb-6">
          You can also check your booking<br />information in your mail.
        </p>
        <button
          onClick={() => navigate('/search-flight')}
          className="w-full py-3 bg-jet-primary text-white rounded-lg font-bold hover:bg-jet-primary-dark transition-colors shadow-md"
        >
          OK
        </button>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
